import dayjs from '@dcp/shared/utils/dayjs'
import { useState } from 'react'
import { FileText, Loader2, RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  type DataTablePagination,
  EmptyState,
} from '@atrvd/saas-ui'
import type { BalanceImportStatus } from '@/types/banking'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { queryErrorDisplay, type TableQueryStateProps } from '@/components/common/queryErrorDisplay'
import { useTriggerLedgerSyncSyncBalanceAccount } from '@/hooks/useLedgerSync'
import { RECONNECT_ELSEWHERE_HINT } from '@/components/ledgersync/ledgerSyncConnectedBanks.utils'
import { EditableBalanceCell } from './EditableBalanceCell'

interface BalanceImportTableProps extends TableQueryStateProps {
  /** Rows for the currently visible page. */
  data: BalanceImportStatus[]
  /**
   * The footer, owned by the page (`useTablePagination().forTotal`); `undefined`
   * hides it (≤ 20 rows, or the total not known yet).
   */
  pagination: DataTablePagination | undefined
}

/**
 * True when a row is a connected (API) account whose last balance sync failed.
 * Drives BOTH the "Resync" button label and the red-row highlight (AC#3), so
 * the two can never disagree.
 */
function isFailedApiRow(row: BalanceImportStatus): boolean {
  return row.isApiAccount && row.apiUpdateStatus === 'failed'
}

/** Marker label and hover text for each connection state that stops balance updates. */
const CONNECTION_MARKER: Record<
  NonNullable<BalanceImportStatus['connectionStatus']>,
  { label: string; title: string }
> = {
  failed: {
    label: 'Connection lost — reconnect',
    title:
      `This bank connection stopped working (expired credentials or consent). The balance shown is the last one received. ${RECONNECT_ELSEWHERE_HINT}`,
  },
  disconnected: {
    label: 'Access revoked — reconnect',
    title:
      `Access to this bank was revoked. The balance shown is the last one received. ${RECONNECT_ELSEWHERE_HINT}`,
  },
  unknown: {
    label: 'Needs attention — reconnect',
    title: `The bank connection needs attention. The balance shown is the last one received. ${RECONNECT_ELSEWHERE_HINT}`,
  },
  requires_action: {
    label: 'Action required — reconnect',
    title:
      `The bank needs you to sign in again or confirm access. The balance shown is the last one received. ${RECONNECT_ELSEWHERE_HINT}`,
  },
}

/**
 * True when the row's bank connection itself stopped delivering — `failed`
 * (credentials or consent died at the bank), `disconnected` (access revoked
 * upstream), `requires_action` (the bank wants the user again) or a status newer
 * than this bundle (`unknown`).
 *
 * Separate from {@link isFailedApiRow}, which describes the last BALANCE SYNC: the
 * two are independent, and the interesting case is a row where the last sync
 * succeeded and the link died afterwards. Such a row keeps counting in Bank
 * Balance / Cash Position Summary (DCP-338), so the table is the only place the
 * user learns the figure stopped being refreshed — the marker, the red row and the
 * dead Sync button must all key off THIS predicate, not off the sync status
 * (PResto 54952 #2).
 */
function isBrokenConnectionRow(row: BalanceImportStatus): boolean {
  return row.connectionStatus !== undefined
}

/**
 * Format timestamp to MM/DD/YYYY, h:mm:ss A with validation.
 * Returns '-' for empty or invalid timestamps.
 */
function formatTimestamp(timestamp: string): string {
  const d = dayjs(timestamp)
  if (!timestamp || !d.isValid()) {
    return '-'
  }
  return d.format('MM/DD/YYYY, h:mm:ss A')
}

/**
 * Balance import status table component (DCP-217).
 *
 * Renders one row per bank account for the current page — manual AND connected
 * accounts in a single unified table (AC#5). Column order is fixed:
 *   Facility, Bank, Account name, Account number, Bank Balance,
 *   API Update Status, Update Source, Last Update Timestamp, Actions.
 *
 * Connected (API) accounts expose a per-row "Sync" button (or "Resync" + a red
 * row when their last balance sync failed) wired to the per-account balance
 * read; manual accounts show no action. Paging is server-side: the owner keeps
 * page and size and hands in the footer (`lib/pagination.ts` rules).
 */
export function BalanceImportTable({
  data,
  pagination,
  loading,
  refreshing,
  error,
  retrying,
  onRetry,
}: BalanceImportTableProps) {
  // Per-account balance-read trigger (DCP-217). A single mutation object is
  // shared across rows, so its `isPending`/`variables` only reflect the LAST
  // click — clicking row B while A is still in flight would wrongly re-enable A.
  // Track the set of in-flight bankAccountIds ourselves so each row's busy state
  // is independent (review #10).
  const syncBalanceAccount = useTriggerLedgerSyncSyncBalanceAccount()
  const [syncingAccountIds, setSyncingAccountIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  /** Start a per-account balance sync, tracking its id as in-flight until it settles. */
  const startAccountSync = (bankAccountId: string) => {
    setSyncingAccountIds((prev) => new Set(prev).add(bankAccountId))
    syncBalanceAccount.mutate(bankAccountId, {
      onSettled: () =>
        setSyncingAccountIds((prev) => {
          const next = new Set(prev)
          next.delete(bankAccountId)
          return next
        }),
    })
  }

  // Columns are built inline (not memoized) so the Actions renderer can close
  // over the live mutation state; useTableColumnPrefs reconciles by key
  // CONTENT (signatures), so a fresh array identity each render is fine.
  const columns: DataTableColumn<BalanceImportStatus>[] = [
    {
      key: 'facility',
      header: 'Facility',
    },
    {
      key: 'bank',
      header: 'Bank',
      render: (row) => row.bank || '-',
    },
    {
      key: 'accountName',
      header: 'Account name',
      render: (row) => (
        <Link
          to="#"
          className="text-primary hover:underline"
        >
          {row.accountName}
        </Link>
      ),
    },
    {
      key: 'accountNumber',
      header: 'Account number',
      // Identity column — always kept visible so a row remains identifiable
      // even when the user hides other columns via the Columns menu.
      disableHide: true,
    },
    {
      key: 'bankBalance',
      header: 'Bank Balance',
      cellClassName: 'tabular-nums',
      // Inline edit lives inside the cell renderer (manual accounts only) — the
      // EditableBalanceCell decides between read-only and editable rendering
      // based on `row.isApiAccount`, and owns its own font/colour styling for
      // negative-value display.
      render: (row) => (
        <EditableBalanceCell
          bankAccountId={row.bankAccountId}
          value={row.bankBalance}
          isApiAccount={row.isApiAccount}
        />
      ),
    },
    {
      key: 'apiUpdateStatus',
      header: 'API Update Status',
      // Manual accounts have no upstream API → "-". Connected accounts show the
      // balance-sync health as a badge, styled like the Intacct balances table:
      // "Loaded" (success), "Failed" (with the raw error surfaced via title on
      // hover), "Pending" otherwise.
      render: (row) => {
        if (!row.isApiAccount) {
          return <span className="text-muted-foreground text-sm">-</span>
        }
        // DCP-338: a BROKEN connection outranks the balance-sync health. The row
        // is deliberately still here and its last balance still counts in Bank
        // Balance / Cash Position Summary, so this marker is the only thing that
        // tells the user the figure stopped being refreshed and what to do about
        // it. Showing the stale "Loaded" from the last successful sync instead
        // would make a dead bank look healthy.
        if (row.connectionStatus) {
          const marker = CONNECTION_MARKER[row.connectionStatus]
          return (
            <span className="text-red-600 text-sm" title={marker.title}>
              {marker.label}
            </span>
          )
        }
        if (row.apiUpdateStatus === 'success') {
          return <Badge variant="success">Loaded</Badge>
        }
        if (row.apiUpdateStatus === 'failed') {
          return (
            <Badge variant="destructive" title={row.apiUpdateMessage || undefined}>
              Failed
            </Badge>
          )
        }
        return <Badge variant="secondary">Pending</Badge>
      },
    },
    {
      key: 'lastUpdateSource',
      header: 'Update Source',
    },
    {
      key: 'lastUpdateTimestamp',
      header: 'Last Update Timestamp',
      render: (row) => formatTimestamp(row.lastUpdateTimestamp),
      cellClassName: 'text-sm',
    },
    {
      key: 'actions',
      header: 'Actions',
      // Kept visible so the Sync affordance is always reachable regardless of
      // the user's saved column visibility (mirrors Account number).
      disableHide: true,
      render: (row) => {
        // Manual accounts have no upstream API to sync — render a placeholder.
        if (!row.isApiAccount) {
          return <span className="text-muted-foreground text-sm">-</span>
        }
        // Per-row busy state from our own in-flight set, so a second row's click
        // never re-enables this row while its own request is still running.
        const isSyncing = syncingAccountIds.has(row.bankAccountId)
        // A failed last sync becomes a "Resync" retry affordance (AC#3).
        const label = isFailedApiRow(row) ? 'Resync' : 'Sync'
        // A broken connection cannot be synced from here: the read would fail
        // upstream, so an enabled button promises a retry that cannot work. The
        // fix is to reconnect the bank from the connection's actions menu in
        // Settings → Financial Accounts — say so in the title rather than
        // leaving a dead click.
        const brokenConnection = isBrokenConnectionRow(row)
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={() => startAccountSync(row.bankAccountId)}
            disabled={isSyncing || brokenConnection}
            title={
              brokenConnection
                ? `This bank’s connection is broken, so it cannot be synced. ${RECONNECT_ELSEWHERE_HINT}`
                : undefined
            }
          >
            {isSyncing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {label}
          </Button>
        )
      },
    },
  ]

  // Persisted show/hide + drag-reorder preferences for this table, keyed
  // per-user in localStorage. Spread onto DataTable below as controlled
  // visibleColumns/columnOrder props.
  const columnPrefs = useTableColumnPrefs('balance-import-manual', columns)

  const { banner, tableError } = queryErrorDisplay({ error, retrying, onRetry }, data.length)

  return (
    <>
      {banner}
      <DataTable
        title="Bank Balances"
        columns={columns}
        data={data}
        rowKey={(row) => row.id}
        pagination={pagination}
        loading={loading}
        refreshing={refreshing}
        error={tableError}
        itemName="accounts"
        // AC#3: a connected account whose last balance sync failed is highlighted
        // red so the "Resync" affordance is visually anchored to the problem row.
        // DCP-338 adds the second kind of problem row — a broken connection —
        // which otherwise showed a red marker inside an unhighlighted row while a
        // merely failed sync got the full highlight (PResto 54952 #2).
        rowClassName={(row) => (isFailedApiRow(row) || isBrokenConnectionRow(row) ? 'bg-red-50' : '')}
        emptyState={
          <EmptyState
            icon={FileText}
            title="No balance import status data available"
          />
        }
        enableColumnReorder
        {...columnPrefs}
      />
    </>
  )
}
